import React, { useState } from "react";
import { 

  FaUniversity, 
  FaPlus, 
  FaExchangeAlt, 
  FaShieldAlt, 
  FaCheckCircle, 
  FaClock, 
  FaTrashAlt, 
  FaEdit,
  FaWallet,
  FaGlobe,
  FaArrowRight,
  FaInfoCircle,
  FaLock,
  FaChevronRight,
  FaBuilding,
  FaUser
} from "react-icons/fa";
import { 
  HiOutlineBanknotes, 
  HiSparkles,
  HiArrowTrendingUp
} from "react-icons/hi2";
import AddressLocationSelector from "./addressLocation/AddressLocationSelector";
import { toLocationValue, fromLocationValue, STRIPE_FIELDS } from "./addressLocation/addressLocationData";
import { elakayAutoText, getElakayLanguageContext } from "./dataElakay";
import { countryDisplayName, stripeBusinessTypeLabel, stripeCapabilityLabel } from "../../utils/stripeDisplayLabels";


let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
// Current language source: the app-wide one (always up to date after a language switch), else
// the infoSource this module last rendered with.
const __elakayLanguageSource = () => getElakayLanguageContext?.() || __elakayInfoSource;
const __elakayText = (key, fallback) =>
  __elakayLanguageSource()?.laguangeElakay?.("frontElakayHardcodedText", __elakayLanguageSource().languageAPI)?.["associatepaymentdashboard"]?.[key] || fallback;

// ============================================
// STYLES
// ============================================

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap');

.associate-payment-dashboard {
  min-height: 100vh;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #1e293b;
  padding: 0;
  position: relative;
  overflow-x: hidden;
}

.apd-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 24px;
  position: relative;
  z-index: 1;
}

/* ===== HEADER SECTION ===== */
.apd-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 40px;
  flex-wrap: wrap;
  gap: 24px;
}

.apd-header-left h1 {
  font-size: 32px;
  font-weight: 800;
  margin: 0 0 8px 0;
  color: #0f172a;
  letter-spacing: -0.5px;
}

.apd-header-left p {
  color: #64748b;
  font-size: 15px;
  margin: 0;
}

.apd-header-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.apd-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 14px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  text-decoration: none;
}

.apd-btn-primary {
  background: linear-gradient(135deg, #007399 0%, #00a4d8 100%);
  color: white;
  box-shadow: 0 4px 20px rgba(0, 115, 153, 0.3);
}

.apd-btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 115, 153, 0.4);
}

.apd-btn-secondary {
  background: #ffffff;
  color: #475569;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.apd-btn-secondary:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.apd-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

/* ===== BALANCE CARD ===== */
.apd-balance-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  padding: 32px;
  margin-bottom: 32px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.apd-balance-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #007399, #00a4d8);
}

.apd-balance-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
}

.apd-balance-label {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
}

.apd-balance-label svg {
  color: #10b981;
}

.apd-balance-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.apd-balance-amount {
  position: relative;
  z-index: 1;
}

.apd-balance-amount h2 {
  font-family: 'Space Mono', monospace;
  font-size: 56px;
  font-weight: 700;
  margin: 0;
  letter-spacing: -2px;
  color: #0f172a;
}

.apd-balance-currency {
  font-size: 24px;
  color: #64748b;
  margin-left: 4px;
}

.apd-balance-footer {
  display: flex;
  gap: 24px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
  position: relative;
  z-index: 1;
}

.apd-balance-stat {
  flex: 1;
}

.apd-balance-stat-label {
  color: #64748b;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.apd-balance-stat-value {
  font-family: 'Space Mono', monospace;
  font-size: 20px;
  font-weight: 600;
  color: #0f172a;
}

/* ===== ACCOUNT INFO CARD ===== */
.apd-account-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 28px;
  margin-bottom: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.apd-account-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.apd-account-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.apd-account-title-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #007399 0%, #00a4d8 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #ffffff;
}

.apd-account-title h3 {
  font-size: 20px;
  font-weight: 700;
  margin: 0 0 4px 0;
  color: #0f172a;
}

.apd-account-title p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.apd-account-edit-link {
  color: #007399;
  font-size: 14px;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.2s;
}

.apd-account-edit-link:hover {
  color: #004b63;
}

.apd-account-action {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid #b8d9e5;
  border-radius: 10px;
  background: #f2fbfe;
  color: #006f93;
  padding: 9px 13px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.apd-account-action.attention {
  border-color: #f5c76b;
  background: #fff8e8;
  color: #925f00;
}

.apd-account-guidance {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: -10px 0 22px;
  padding: 12px 14px;
  border-radius: 11px;
  background: #fff8e8;
  border: 1px solid #f5d58f;
  color: #75500a;
  font-size: 13px;
  line-height: 1.45;
}

.apd-action-help {
  flex-basis: 100%;
  margin: -12px 0 0;
  color: #64748b;
  font-size: 12px;
  text-align: right;
}

.apd-account-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.apd-account-item {
  background: #f8fafc;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.apd-account-item-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94a3b8;
  margin-bottom: 6px;
}

.apd-account-item-value {
  font-size: 15px;
  font-weight: 600;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
}

.apd-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.apd-status-enabled {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.apd-status-disabled {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.apd-status-active {
  background: rgba(0, 115, 153, 0.12);
  color: #007399;
}

/* ===== BANK ACCOUNTS SECTION ===== */
.apd-section {
  margin-bottom: 32px;
}

.apd-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.apd-section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: #0f172a;
}

.apd-section-title svg {
  color: #007399;
}

.apd-bank-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* ===== BANK CARD ===== */
.apd-bank-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.apd-bank-card:hover {
  border-color: rgba(0, 115, 153, 0.3);
  box-shadow: 0 8px 30px rgba(0, 115, 153, 0.1);
}

.apd-bank-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.apd-bank-card-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  font-size: 16px;
  color: #0f172a;
}

.apd-bank-card-title svg {
  color: #007399;
}

.apd-bank-card-meta {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 14px;
  color: #64748b;
}

.apd-bank-default-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.15) 100%);
  color: #10b981;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.apd-bank-card-body {
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 24px;
  padding: 24px;
}

@media (max-width: 768px) {
  .apd-bank-card-body {
    grid-template-columns: 1fr;
  }
}

/* ===== VISUAL BANK CARD ===== */
.apd-visual-card {
  width: 100%;
  max-width: 340px;
  height: 200px;
  background: linear-gradient(135deg, #1e3a5f 0%, #0d1b2a 50%, #1b263b 100%);
  border-radius: 20px;
  padding: 24px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.apd-visual-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(ellipse at 30% 20%, rgba(99, 102, 241, 0.3) 0%, transparent 50%),
    radial-gradient(ellipse at 70% 80%, rgba(16, 185, 129, 0.2) 0%, transparent 50%);
  pointer-events: none;
}

.apd-visual-card::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
  opacity: 0.03;
  pointer-events: none;
}

.apd-visual-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
  z-index: 1;
}

.apd-visual-chip {
  width: 50px;
  height: 38px;
  background: linear-gradient(135deg, #d4af37 0%, #f9e076 50%, #d4af37 100%);
  border-radius: 8px;
  position: relative;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.apd-visual-chip::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 8px;
  right: 8px;
  height: 1px;
  background: rgba(0, 0, 0, 0.2);
}

.apd-visual-chip::after {
  content: '';
  position: absolute;
  top: 8px;
  bottom: 8px;
  left: 50%;
  width: 1px;
  background: rgba(0, 0, 0, 0.2);
}

.apd-visual-bank-logo {
  font-size: 18px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.apd-visual-card-middle {
  position: relative;
  z-index: 1;
}

.apd-visual-account-number {
  font-family: 'Space Mono', monospace;
  font-size: 22px;
  letter-spacing: 3px;
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 8px;
}

.apd-visual-routing {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
}

.apd-visual-card-bottom {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  position: relative;
  z-index: 1;
}

.apd-visual-country {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
}

.apd-visual-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
}

.apd-visual-status.verified {
  color: #10b981;
}

.apd-visual-status.pending {
  color: #f59e0b;
}

/* ===== BANK INFO PANEL ===== */
.apd-bank-info-panel {
  display: flex;
  flex-direction: column;
}

.apd-bank-info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

@media (max-width: 480px) {
  .apd-bank-info-grid {
    grid-template-columns: 1fr;
  }
}

.apd-bank-info-item {
  background: #f8fafc;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.apd-bank-info-item-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #94a3b8;
  margin-bottom: 4px;
}

.apd-bank-info-item-value {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}

.apd-bank-billing {
  background: rgba(0, 115, 153, 0.06);
  border: 1px solid rgba(0, 115, 153, 0.15);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
}

.apd-bank-billing-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #007399;
  margin-bottom: 10px;
}

.apd-bank-billing-address {
  font-size: 14px;
  color: #475569;
  line-height: 1.6;
}

.apd-bank-actions {
  display: flex;
  gap: 12px;
  margin-top: auto;
}

.apd-bank-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.apd-bank-action-delete {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.apd-bank-action-delete:hover {
  background: rgba(239, 68, 68, 0.18);
}

.apd-bank-action-edit {
  background: rgba(0, 115, 153, 0.1);
  color: #007399;
  border: 1px solid rgba(0, 115, 153, 0.2);
}

.apd-bank-action-edit:hover {
  background: rgba(0, 115, 153, 0.18);
}

.apd-bank-action-default {
  background: transparent;
  color: #059669;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.apd-bank-action-default:hover {
  background: rgba(16, 185, 129, 0.1);
}

/* ===== EMPTY STATE ===== */
.apd-empty-state {
  text-align: center;
  padding: 60px 24px;
  background: #f8fafc;
  border: 2px dashed #cbd5e1;
  border-radius: 20px;
}

.apd-empty-icon {
  width: 80px;
  height: 80px;
  background: rgba(0, 115, 153, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 32px;
  color: #007399;
}

.apd-empty-state h4 {
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #0f172a;
}

.apd-empty-state p {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

/* ===== SECURITY FOOTER ===== */
.apd-security-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 20px;
  background: rgba(16, 185, 129, 0.06);
  border: 1px solid rgba(16, 185, 129, 0.15);
  border-radius: 14px;
  margin-top: 32px;
}

.apd-security-footer svg {
  color: #059669;
  font-size: 18px;
}

.apd-security-footer span {
  color: #475569;
  font-size: 13px;
}

/* ===== ONBOARDING FORM ===== */
.apd-onboarding {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.apd-onboarding-header {
  text-align: center;
  margin-bottom: 40px;
}

.apd-onboarding-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #007399 0%, #00a4d8 100%);
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 36px;
  color: #ffffff;
  box-shadow: 0 10px 30px rgba(0, 115, 153, 0.25);
}

.apd-onboarding-header h2 {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: #0f172a;
}

.apd-onboarding-header p {
  color: #64748b;
  font-size: 15px;
  margin: 0;
}

.apd-form-group {
  margin-bottom: 24px;
}

.apd-form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 8px;
}

.apd-form-input,
.apd-form-select {
  width: 100%;
  padding: 14px 18px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 12px;
  color: #0f172a;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 15px;
  transition: all 0.2s;
}

.apd-form-input:focus,
.apd-form-select:focus {
  outline: none;
  border-color: #007399;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(0, 115, 153, 0.12);
}

.apd-form-input::placeholder {
  color: #94a3b8;
}

.apd-form-select option {
  background: #ffffff;
  color: #0f172a;
}

.apd-form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 480px) {
  .apd-form-row {
    grid-template-columns: 1fr;
  }
}

.apd-form-divider {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 32px 0;
  color: #94a3b8;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.apd-form-divider::before,
.apd-form-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e2e8f0;
}

.apd-onboarding .apd-form-link {
  appearance: none;
  background: #f0f9fc;
  border: 1px solid #bfdde8;
  border-radius: 12px;
  color: #007399;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  max-width: 100%;
  padding: 8px 14px 8px 8px;
  margin-top: 12px;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s, box-shadow 0.2s;
}

.apd-onboarding .apd-form-link:hover {
  background: #e3f3f9;
  border-color: #007399;
  color: #004b63;
  box-shadow: 0 3px 10px rgba(0, 115, 153, 0.1);
}

.apd-onboarding .apd-form-link:focus-visible {
  outline: 3px solid #007399;
  outline-offset: 3px;
}

.apd-onboarding .apd-form-link:active {
  background: #d6edf6;
  box-shadow: none;
}

.apd-form-link-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 32px;
  height: 32px;
  border-radius: 9px;
  background: #007399;
  color: #ffffff;
}

@media (max-width: 480px) {
  .apd-onboarding .apd-form-link {
    width: 100%;
  }
}

.apd-form-submit {
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #007399 0%, #00a4d8 100%);
  border: none;
  border-radius: 14px;
  color: white;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 32px;
  box-shadow: 0 4px 20px rgba(0, 115, 153, 0.3);
}

.apd-form-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 115, 153, 0.4);
}

.apd-form-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.apd-form-submit .spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ===== BUSINESS TYPE CARDS ===== */
.apd-business-type-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 12px;
}

@media (max-width: 480px) {
  .apd-business-type-cards {
    grid-template-columns: 1fr;
  }
}

.apd-business-type-card {
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s;
  text-align: center;
}

.apd-business-type-card:hover {
  border-color: rgba(0, 115, 153, 0.3);
  background: rgba(0, 115, 153, 0.05);
}

.apd-business-type-card.selected {
  border-color: #007399;
  background: rgba(0, 115, 153, 0.08);
}

.apd-business-type-card svg {
  font-size: 32px;
  color: #007399;
  margin-bottom: 12px;
}

.apd-business-type-card h4 {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #0f172a;
}

.apd-business-type-card p {
  font-size: 12px;
  color: #64748b;
  margin: 0;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .apd-container {
    padding: 24px 16px;
  }
  
  .apd-header {
    flex-direction: column;
    align-items: stretch;
  }
  
  .apd-header-actions {
    justify-content: stretch;
  }
  
  .apd-header-actions .apd-btn {
    flex: 1;
    justify-content: center;
  }
  
  .apd-balance-card {
    padding: 24px;
  }
  
  .apd-balance-amount h2 {
    font-size: 40px;
  }
  
  .apd-balance-footer {
    flex-direction: column;
    gap: 16px;
  }
  
  .apd-onboarding {
    padding: 24px;
  }
}
`;

// ============================================
// COMPONENTS
// ============================================

/**
 * Balance Card Component
 */
const BalanceCard = ({ total = 0, pending = 0, available = 0 }) => {
  return (
    <div className="apd-balance-card">
      <div className="apd-balance-header">
        <div className="apd-balance-label">
          <HiOutlineBanknotes size={20} />
          <span>{__elakayText("t001", "Available Balance")}</span>
        </div>
        <div className="apd-balance-badge">
          <HiSparkles size={14} />
          <span>{__elakayText("t002", "Associate Earnings")}</span>
        </div>
      </div>
      
      <div className="apd-balance-amount">
        <h2>
          ${total?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          <span className="apd-balance-currency">USD</span>
        </h2>
      </div>
      
      <div className="apd-balance-footer">
        <div className="apd-balance-stat">
          <div className="apd-balance-stat-label">{__elakayText("t003", "Pending")}</div>
          <div className="apd-balance-stat-value">
            ${pending?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
        </div>
        <div className="apd-balance-stat">
          <div className="apd-balance-stat-label">{__elakayText("t004", "Available for Transfer")}</div>
          <div className="apd-balance-stat-value">
            ${available?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
        </div>
        <div className="apd-balance-stat">
          <div className="apd-balance-stat-label">{__elakayText("t005", "This Month")}</div>
          <div className="apd-balance-stat-value" style={{ color: '#10b981' }}>
            <HiArrowTrendingUp style={{ marginRight: 4 }} />
            +12.5%
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Account Info Card Component
 */
const AccountInfoCard = ({ account, onEditAccount }) => {
  if (!account) return null;
  const accountReady = account.payouts_enabled === true &&
    account.capabilities?.transfers === 'active';

  return (
    <div className="apd-account-card">
      <div className="apd-account-header">
        <div className="apd-account-title">
          <div className="apd-account-title-icon">
            <FaWallet />
          </div>
          <div>
            <h3>{__elakayText("t006", "Payment Account")}</h3>
            <p>{__elakayText("t052", "ID:")}{account.id}</p>
          </div>
        </div>
        <button
          type="button"
          className={`apd-account-action${accountReady ? '' : ' attention'}`}
          onClick={() => onEditAccount?.()}
        >
          {accountReady ? <FaEdit /> : <FaShieldAlt />}
          {accountReady
            ? __elakayText("t053", "Manage account")
            : elakayAutoText("h_complete_stripe_setup", "Complete verification")}
        </button>
      </div>

      {!accountReady && (
        <div className="apd-account-guidance" role="status">
          <FaInfoCircle aria-hidden="true" />
          <span>{elakayAutoText("h_stripe_activation_help", "Complete the required information with Stripe to activate payouts and transfers. These statuses cannot be enabled manually in Elakay.")}</span>
        </div>
      )}
      
      <div className="apd-account-grid">
        <div className="apd-account-item">
          <div className="apd-account-item-label">{__elakayText("t007", "Email")}</div>
          <div className="apd-account-item-value">
            {account.email || elakayAutoText("h_20a0418296f546", "Not provided")}
          </div>
        </div>
        
        <div className="apd-account-item">
          <div className="apd-account-item-label">{__elakayText("t008", "Business Type")}</div>
          <div className="apd-account-item-value" style={{ textTransform: 'capitalize' }}>
            {account.business_type === 'individual' ? <FaUser style={{ marginRight: 6 }} /> : <FaBuilding style={{ marginRight: 6 }} />}
            {account.business_type ? stripeBusinessTypeLabel(account.business_type) : "—"}
          </div>
        </div>
        
        <div className="apd-account-item">
          <div className="apd-account-item-label">{__elakayText("t009", "Country")}</div>
          <div className="apd-account-item-value">
            <FaGlobe style={{ marginRight: 6, opacity: 0.6 }} />
            {account.country ? countryDisplayName(account.country) : "—"}
          </div>
        </div>
        
        <div className="apd-account-item">
          <div className="apd-account-item-label">{__elakayText("t010", "Payouts")}</div>
          <div className="apd-account-item-value">
            <span className={`apd-status-badge ${account.payouts_enabled ? 'apd-status-enabled' : 'apd-status-disabled'}`}>
              {account.payouts_enabled ? <><FaCheckCircle />{__elakayText("t011", "Enabled")}</> : elakayAutoText("h_f4f4473df8cb59", "Disabled")}
            </span>
          </div>
        </div>
        
        <div className="apd-account-item">
          <div className="apd-account-item-label">{__elakayText("t012", "Transfers")}</div>
          <div className="apd-account-item-value">
            <span className={`apd-status-badge ${account.capabilities?.transfers === 'active' ? 'apd-status-active' : 'apd-status-disabled'}`}>
              {account.capabilities?.transfers ? stripeCapabilityLabel(account.capabilities.transfers) : "—"}
            </span>
          </div>
        </div>
        
        <div className="apd-account-item">
          <div className="apd-account-item-label">{__elakayText("t013", "Card Payments")}</div>
          <div className="apd-account-item-value">
            <span className={`apd-status-badge ${account.capabilities?.card_payments === 'active' ? 'apd-status-active' : 'apd-status-disabled'}`}>
              {account.capabilities?.card_payments ? stripeCapabilityLabel(account.capabilities.card_payments) : "—"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Bank Account Card Component
 */
const BankAccountCard = ({ bank, onDelete, onSetDefault, onUpdate }) => {
  const masked = `•••• •••• •••• ${bank.last4 || "????"}`;
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [accountHolderName, setAccountHolderName] = useState(bank.account_holder_name || "");
  const [accountHolderType, setAccountHolderType] = useState(bank.account_holder_type || "individual");

  const handleSave = async () => {
    setSaving(true);
    await onUpdate?.(bank.id, {
      account_holder_name: accountHolderName,
      account_holder_type: accountHolderType,
    });
    setSaving(false);
    setIsEditing(false);
  };

  return (
    <div className="apd-bank-card">
      <div className="apd-bank-card-header">
        <div className="apd-bank-card-title">
          <FaUniversity />
          <span>{bank.bank_name || elakayAutoText("h_6d391e1333d9e0", "Bank Account")}</span>
        </div>
        <div className="apd-bank-card-meta">
          <span>{bank.currency?.toUpperCase()}</span>
          {bank.default_for_currency && (
            <span className="apd-bank-default-badge">
              <FaCheckCircle size={10} />{__elakayText("t060", "Default")}</span>
          )}
        </div>
      </div>
      
      <div className="apd-bank-card-body">
        {/* Visual Card */}
        <div className="apd-visual-card">
          <div className="apd-visual-card-top">
            <div className="apd-visual-chip" />
            <div className="apd-visual-bank-logo">
              {bank.bank_name || "BANK"}
            </div>
          </div>
          
          <div className="apd-visual-card-middle">
            <div className="apd-visual-account-number">{masked}</div>
            <div className="apd-visual-routing">{__elakayText("t054", "Routing:")}{bank.routing_number || "—"}
            </div>
          </div>
          
          <div className="apd-visual-card-bottom">
            <div className="apd-visual-country">{bank.country}</div>
            <div className={`apd-visual-status ${bank.status === 'verified' ? 'verified' : 'pending'}`}>
              {bank.status === 'verified' ? (
                <><FaCheckCircle />{__elakayText("t014", "Verified")}</>
              ) : (
                <><FaClock />{__elakayText("t003", "Pending")}</>
              )}
            </div>
          </div>
        </div>
        
        {/* Info Panel */}
        <div className="apd-bank-info-panel">
          <div className="apd-bank-info-grid">
            <div className="apd-bank-info-item">
              <div className="apd-bank-info-item-label">{__elakayText("t016", "Bank Name")}</div>
              <div className="apd-bank-info-item-value">{bank.bank_name || "—"}</div>
            </div>
            <div className="apd-bank-info-item">
              <div className="apd-bank-info-item-label">{__elakayText("t009", "Country")}</div>
              <div className="apd-bank-info-item-value">{bank.country || "—"}</div>
            </div>
            <div className="apd-bank-info-item">
              <div className="apd-bank-info-item-label">{__elakayText("t018", "Currency")}</div>
              <div className="apd-bank-info-item-value">{bank.currency?.toUpperCase() || "—"}</div>
            </div>
            <div className="apd-bank-info-item">
              <div className="apd-bank-info-item-label">{__elakayText("t019", "Last 4 Digits")}</div>
              <div className="apd-bank-info-item-value">{bank.last4 || "—"}</div>
            </div>
          </div>
          
          {bank.billing_address && (
            <div className="apd-bank-billing">
              <div className="apd-bank-billing-title">
                <FaShieldAlt />{__elakayText("t055", "Billing Address")}</div>
              <div className="apd-bank-billing-address">
                {bank.billing_address.name}<br />
                {bank.billing_address.line1}<br />
                {bank.billing_address.city}, {bank.billing_address.state} {bank.billing_address.postal_code}<br />
                {bank.billing_address.country}
              </div>
            </div>
          )}

          {isEditing && (
            <div className="apd-bank-billing">
              <div className="apd-form-group">
                <label className="apd-form-label">{__elakayText("t062", "Account holder name")}</label>
                <input
                  type="text"
                  className="apd-form-input"
                  value={accountHolderName}
                  onChange={(e) => setAccountHolderName(e.target.value)}
                />
              </div>
              <div className="apd-form-group">
                <label className="apd-form-label">{__elakayText("t063", "Account holder type")}</label>
                <select
                  className="apd-form-select"
                  value={accountHolderType}
                  onChange={(e) => setAccountHolderType(e.target.value)}
                >
                  <option value="individual">{__elakayText("t029", "Individual")}</option>
                  <option value="company">{__elakayText("t031", "Company")}</option>
                </select>
              </div>
            </div>
          )}

          <div className="apd-bank-actions">
            <button
              className="apd-bank-action-btn apd-bank-action-delete"
              onClick={() => onDelete && onDelete(bank.id)}
            >
              <FaTrashAlt />{__elakayText("t060", "Delete")}</button>
            {isEditing ? (
              <>
                <button
                  className="apd-bank-action-btn apd-bank-action-default"
                  disabled={saving}
                  onClick={handleSave}
                >
                  <FaCheckCircle />{saving ? __elakayText("t064", "Saving…") : __elakayText("t065", "Save")}</button>
                <button
                  className="apd-bank-action-btn apd-bank-action-edit"
                  onClick={() => setIsEditing(false)}
                >
                  {__elakayText("t066", "Cancel")}
                </button>
              </>
            ) : (
              <button
                className="apd-bank-action-btn apd-bank-action-edit"
                onClick={() => setIsEditing(true)}
              >
                <FaEdit />{__elakayText("t061", "Edit")}</button>
            )}
            {!bank.default_for_currency && (
              <button
                className="apd-bank-action-btn apd-bank-action-default"
                onClick={() => onSetDefault && onSetDefault(bank.id)}
              >
                <FaCheckCircle />{__elakayText("t056", "Set Default")}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Empty State Component
 */
const EmptyState = () => (
  <div className="apd-empty-state">
    <div className="apd-empty-icon">
      <FaUniversity />
    </div>
    <h4>{__elakayText("t020", "No Bank Accounts Yet")}</h4>
    <p>{__elakayText("t021", "Add a bank account to receive your associate payouts")}</p>
  </div>
);

/**
 * Onboarding Form Component
 */
const OnboardingForm = ({ 
  address = [], 
  token, 
  AccountStripeCreate, 
  loadingB, 
  infoSource 
}) => {
  __setElakayInfoSource(infoSource);
  const [values, setValues] = useState({
    country: "",
    business_type: "",
    company_name: "",
    individual: undefined,
    company: undefined,
  });
  const [addressA, setAddressA] = useState(false);

  // Validation functions
  function allFieldsFilled(data) {
    if (data === null || data === undefined) return false;
    if (typeof data !== "object") {
      return data !== "" && data !== null && data !== undefined;
    }
    return Object.values(data).every((value) => allFieldsFilled(value));
  }

  function validateStripeValues(v) {
    const { business_type, country } = v;
    if (!business_type || !country) return false;
    if (business_type === "company") {
      if (!v.company_name) return false;
      if (!v.company) return false;
      return allFieldsFilled(v.company);
    }
    if (business_type === "individual") {
      if (!v.individual) return false;
      return allFieldsFilled(v.individual);
    }
    return false;
  }

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setValues((prev) => {
      let updated = { ...prev };
      const type = prev.business_type || "individual";

      if (field === "business_type") {
        updated.business_type = value;
        updated.individual = undefined;
        updated.company = undefined;
        if (value !== "company") delete updated.company_name;
        return updated;
      }

      if (field === "company_name") {
        if (type === "company") {
          updated.company_name = value;
          updated.company = { ...(prev.company || {}), name: value };
        } else {
          delete updated.company_name;
        }
        return updated;
      }

      if (field === "country") {
        updated.country = value;
        if (prev[type]?.address) {
          updated[type] = {
            ...(prev[type] || {}),
            address: { ...prev[type].address, country: value },
          };
        }
        return updated;
      }

      const addressFields = ["city", "line1", "postal_code", "state"];
      if (addressFields.includes(field)) {
        updated[type] = {
          ...(prev[type] || {}),
          address: {
            ...(prev[type]?.address || {}),
            country: prev.country || updated.country || "",
            [field]: value,
          },
        };
        return updated;
      }

      if (type === "individual" && (field === "first_name" || field === "last_name")) {
        updated.individual = { ...(prev.individual || {}), [field]: value };
        updated[field] = value;
        return updated;
      }

      updated[field] = value;
      return updated;
    });
  };

  // Shared address location fields: the country is also the account's
  // main country (same as handleChange("country")).
  const handleLocationChange = (location) => {
    setValues((prev) => {
      const type = prev.business_type || "individual";
      return {
        ...prev,
        country: location.country,
        [type]: {
          ...(prev[type] || {}),
          address: fromLocationValue(prev[type]?.address || {}, location, STRIPE_FIELDS),
        },
      };
    });
  };

  const selectAdress = (e) => {
    const id = e.target.value;
    if (!id || id === "0") {
      setValues((prev) => {
        const type = prev.business_type || "individual";
        return { ...prev, [type]: { ...(prev[type] || {}), address: undefined } };
      });
      return;
    }

    const selected = address.find((a) => a._id === id);
    if (!selected) return;

    setValues((prev) => {
      const type = prev.business_type || "individual";
      let updated = { ...prev, individual: undefined, company: undefined };

      if (type === "individual") {
        updated.individual = {
          ...(prev.individual || {}),
          first_name: selected.firstName,
          last_name: selected.lastName,
          address: {
            _id: selected._id,
            city: selected.city,
            country: selected.country,
            line1: selected.Address1,
            postal_code: selected.postalCode,
            state: selected.State,
          },
        };
      }

      if (type === "company") {
        updated.company = {
          ...(prev.company || {}),
          name: prev.company_name,
          address: {
            _id: selected._id,
            city: selected.city,
            country: selected.country,
            line1: selected.Address1,
            postal_code: selected.postalCode,
            state: selected.State,
          },
        };
      }

      return updated;
    });
  };

  const isValid = validateStripeValues(values);

  return (
    <div className="apd-onboarding">
      <div className="apd-onboarding-header">
        <div className="apd-onboarding-icon">
          <FaWallet />
        </div>
        <h2>{__elakayText("t022", "Set Up Your Payout Account")}</h2>
        <p>{__elakayText("t023", "Complete the form below to start receiving your associate earnings")}</p>
      </div>

      <form onSubmit={(e) => e.preventDefault()}>
        {/* Country Selection */}
        <div className="apd-form-group">
          <label className="apd-form-label">{__elakayText("t009", "Country")}</label>
          <select
            className="apd-form-select"
            value={values.country}
            onChange={handleChange("country")}
          >
            <option value="">{__elakayText("t025", "Select your country")}</option>
            <option value="US">{__elakayText("t026", "United States")}</option>
            <option value="CA">{__elakayText("t027", "Canada")}</option>
          </select>
        </div>

        {/* Business Type Selection */}
        <div className="apd-form-group">
          <label className="apd-form-label">{__elakayText("t028", "Account Type")}</label>
          <div className="apd-business-type-cards">
            <div 
              className={`apd-business-type-card ${values.business_type === 'individual' ? 'selected' : ''}`}
              onClick={() => handleChange("business_type")({ target: { value: 'individual' }})}
            >
              <FaUser />
              <h4>{__elakayText("t029", "Individual")}</h4>
              <p>{__elakayText("t030", "For personal accounts")}</p>
            </div>
            <div 
              className={`apd-business-type-card ${values.business_type === 'company' ? 'selected' : ''}`}
              onClick={() => handleChange("business_type")({ target: { value: 'company' }})}
            >
              <FaBuilding />
              <h4>{__elakayText("t031", "Company")}</h4>
              <p>{__elakayText("t032", "For business accounts")}</p>
            </div>
          </div>
        </div>

        {/* Company Name (only for company) */}
        {values.business_type === "company" && (
          <div className="apd-form-group">
            <label className="apd-form-label">{__elakayText("t033", "Company Name")}</label>
            <input
              type="text"
              className="apd-form-input"
              value={values.company_name || ""}
              onChange={handleChange("company_name")}
              placeholder={__elakayText("t034", "Enter your registered company name")}
            />
          </div>
        )}

        <div className="apd-form-divider">{__elakayText("t035", "Address Information")}</div>

        {/* Address Selection or Manual Form */}
        {!addressA ? (
          <div className="apd-form-group">
            <label className="apd-form-label">
              {infoSource?.laguangeElakay?.("title", infoSource.languageAPI)?.billingAddress || elakayAutoText("h_48af96f6cac7fc", "Billing Address")}
            </label>
            <select
              className="apd-form-select"
              value={values[values?.business_type]?.address?._id || "0"}
              onChange={selectAdress}
            >
              <option value="0">
                {infoSource?.laguangeElakay?.("label", infoSource.languageAPI)?.pleaseSelect || elakayAutoText("h_a2e82f4a24678d", "Select an address")}
              </option>
              {address?.map((Adre, i) => (
                <option key={Adre._id || i} value={Adre._id}>
                  ({Adre.firstName} {Adre.lastName}) {Adre.Address1}, {Adre.city} {Adre.State} {Adre.postalCode} {Adre.country}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="apd-form-link"
              onClick={() => setAddressA(true)}
            >
              <span className="apd-form-link-icon" aria-hidden="true"><FaPlus size={14} /></span>
              <span>{__elakayText("t057", "Add new address manually")}</span>
            </button>
          </div>
        ) : (
          <>
            <div className="apd-form-group">
              <label className="apd-form-label">{__elakayText("t036", "Street Address")}</label>
              <input
                type="text"
                className="apd-form-input"
                onChange={handleChange("line1")}
                placeholder={__elakayText("t037", "123 Main Street")}
              />
            </div>

            {/* Country / ZIP / State / City: shared address location fields
                (Stripe Connect accounts stay limited to US / CA). */}
            <div className="apd-form-row">
              <AddressLocationSelector
                idPrefix="apd-business-address"
                value={toLocationValue({ ...(values[values.business_type || "individual"]?.address || {}), country: values.country }, STRIPE_FIELDS)}
                onChange={handleLocationChange}
                countryCodes={["US", "CA"]}
                emptyCountryLabel={__elakayText("t044", "Select country")}
                labels={{ country: __elakayText("t009", "Country"), city: __elakayText("t038", "City") }}
                classNames={{ field: "apd-form-group", label: "apd-form-label", input: "apd-form-input", select: "apd-form-select" }}
              />
            </div>

            <button
              type="button"
              className="apd-form-link"
              onClick={() => setAddressA(false)}
            >
              <span className="apd-form-link-icon" aria-hidden="true"><FaChevronRight size={14} /></span>
              <span>{__elakayText("t058", "Select from saved addresses")}</span>
            </button>
          </>
        )}

        {/* Submit Button */}
        <button
          type="button"
          className="apd-form-submit"
          disabled={!isValid || loadingB}
          onClick={() => {
            if (!loadingB && isValid) AccountStripeCreate(values);
          }}
        >
          {loadingB ? (
            <>
              <div className="spinner" />
              <span>{__elakayText("t047", "Creating Account...")}</span>
            </>
          ) : (
            <>
              <span>{__elakayText("t048", "Create Payout Account")}</span>
              <FaArrowRight />
            </>
          )}
        </button>
      </form>
    </div>
  );
};

/**
 * Security Footer Component
 */
const SecurityFooter = () => (
  <div className="apd-security-footer">
    <FaLock />
    <span>{__elakayText("t049", "Your financial information is encrypted and securely processed by our payment partner")}</span>
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================
const AssociatePaymentDashboard = ({
  userInfo,
  incommePayroll = { total: 0, pending: 0, available: 0 },
  getACCStripe,
  infoSource,
  address,
  token,
  AccountStripeCreate,
  loadingB,
  setAddExBankAcc,
  setTransferMoneyShow,
  Seller,
  bankingL,
  onDeleteBank,
  onSetDefaultBank,
  onUpdateBank,
  onEditAccount
}) => {
  // Only render if user is a seller and has banking enabled
  if (!(Seller?.() && bankingL)) return null;

  const hasAccount = userInfo?.stripeAccountsId;
  const banks = getACCStripe?.external_accounts?.data || [];
  const accountReady = Boolean(
    hasAccount &&
    getACCStripe?.payouts_enabled === true &&
    getACCStripe?.capabilities?.transfers === 'active'
  );
  const hasBank = banks.length > 0;
  const availableAmount = Number(incommePayroll?.available ?? incommePayroll?.total ?? 0);
  const canAddBank = Boolean(hasAccount);
  const canTransfer = accountReady && hasBank && availableAmount > 0;
  const transferHelp = !hasAccount
    ? elakayAutoText("h_create_payout_account_first", "Create your payout account first.")
    : !accountReady
      ? elakayAutoText("h_verify_payout_account_first", "Complete Stripe verification to enable transfers.")
      : !hasBank
        ? elakayAutoText("h_add_bank_first", "Add a bank account to enable transfers.")
        : availableAmount <= 0
          ? elakayAutoText("h_no_transfer_balance", "No balance is currently available for transfer.")
          : "";

  return (
    <>
      <style>{styles}</style>
      <div className="associate-payment-dashboard">
        <div className="apd-container">
          {/* Header */}
          <div className="apd-header">
            <div className="apd-header-left">
              <h1>{__elakayText("t050", "Associate Payments")}</h1>
              <p>{__elakayText("t051", "Manage your earnings and payout methods")}</p>
            </div>
            <div className="apd-header-actions">
              <button
                className={`apd-btn apd-btn-secondary ${!canAddBank ? 'disabled' : ''}`}
                disabled={!canAddBank}
                title={!canAddBank ? transferHelp : undefined}
                onClick={() => canAddBank && setAddExBankAcc(true)}
              >
                <FaPlus /> {infoSource?.laguangeElakay?.('Button', infoSource.languageAPI)?.addBank || elakayAutoText("h_c40e1c1b3af622", "Add Bank")}
              </button>
              <button
                className={`apd-btn apd-btn-primary ${!canTransfer ? 'disabled' : ''}`}
                disabled={!canTransfer}
                title={!canTransfer ? transferHelp : undefined}
                onClick={() => canTransfer && setTransferMoneyShow(true)}
              >
                <FaExchangeAlt /> {infoSource?.laguangeElakay?.('Button', infoSource.languageAPI)?.transfer || elakayAutoText("h_cbb4cc49824bf7", "Transfer")}
              </button>
              {!canTransfer && <p className="apd-action-help">{transferHelp}</p>}
            </div>
          </div>

          {hasAccount ? (
            <>
              {/* Balance Card */}
              <BalanceCard 
                total={incommePayroll?.total || 0}
                pending={incommePayroll?.pending || 0}
                available={incommePayroll?.available || incommePayroll?.total || 0}
              />

              {/* Account Info */}
              <AccountInfoCard account={getACCStripe} onEditAccount={onEditAccount} />

              {/* Bank Accounts Section */}
              <div className="apd-section">
                <div className="apd-section-header">
                  <h3 className="apd-section-title">
                    <FaUniversity />{__elakayText("t059", "Bank Accounts")}</h3>
                </div>

                <div className="apd-bank-list">
                  {banks.length === 0 ? (
                    <EmptyState />
                  ) : (
                    banks.map((bank) => (
                      <BankAccountCard
                        key={bank.id}
                        bank={bank}
                        onDelete={onDeleteBank}
                        onSetDefault={onSetDefaultBank}
                        onUpdate={onUpdateBank}
                      />
                    ))
                  )}
                </div>
              </div>
            </>
          ) : (
            /* Onboarding Form */
            <OnboardingForm
              address={address}
              token={token}
              AccountStripeCreate={AccountStripeCreate}
              loadingB={loadingB}
              infoSource={infoSource}
            />
          )}

          {/* Security Footer */}
          <SecurityFooter />
        </div>
      </div>
    </>
  );
};

export default AssociatePaymentDashboard;
